import {
  isExperimentationEnabled,
  isHideAdminPortalEnabled,
} from "@/lib/config";
import File from "@/lib/components/ui/icons/file";
import Shield from "@/lib/components/ui/icons/shield";
import SquareUser from "@/lib/components/ui/icons/square-user";
import {
  AppWindowMac,
  EyeOff,
  Palette,
  ScanFace,
  SlidersHorizontal,
  SquareCode,
  UserPlus2,
  Users2,
  Webhook,
} from "lucide-react";
import { lazy } from "react";
import { Navigate, useParams, type RouteObject } from "react-router";

const ProfilePage = lazy(() => import("./pages/profile/profile"));
const PrivacySecurityPage = lazy(
  () => import("./pages/privacy-security/privacy-security"),
);
const BrandingPage = lazy(() => import("./pages/branding"));
const AccountDetailsPage = lazy(() => import("./pages/account-details"));
const UsersPage = lazy(() => import("./pages/users/users"));
const GroupsPage = lazy(() => import("./pages/groups/groups"));
const RolesPage = lazy(() => import("./pages/roles"));
const SSOPage = lazy(() => import("./pages/sso"));
const AuditLogsPage = lazy(() => import("./pages/audit-logs"));
const WebhooksPage = lazy(() => import("./pages/webhooks"));
const APITokensPage = lazy(() => import("./pages/api-tokens"));
// PURE so the unused lazy() call (and its chunk) drops out of the bundle
// when experimentation is off
const RedactionPage = /*#__PURE__*/ lazy(() => import("./pages/redaction"));

export type SettingsRouteItem = {
  path: string;
  Icon: React.ElementType;
  titleKey: string;
  disabled?: boolean;
  /** Only visible to Admin and SuperAdmin; hidden from everyone else */
  adminOnly?: boolean;
  prototype?: boolean;
  component: React.ComponentType;
  descriptionKey: string;
};

type SettingsRouteGroup = {
  name: string;
  path?: string;
  items: SettingsRouteItem[];
};

export const settingsRouteGroups: SettingsRouteGroup[] = [
  {
    name: "my-account",
    items: [
      {
        path: "profile",
        Icon: SquareUser,
        titleKey: "settings_page_profile_title",
        component: ProfilePage,
        descriptionKey: "settings_page_profile_description",
      },
      {
        path: "privacy-security",
        Icon: Shield,
        titleKey: "settings_page_privacy_security_title",
        component: PrivacySecurityPage,
        descriptionKey: "settings_page_privacy_security_description",
      },
      {
        path: "branding",
        Icon: Palette,
        adminOnly: true,
        titleKey: "settings_page_branding_title",
        component: BrandingPage,
        descriptionKey: "settings_page_branding_description",
      },
      ...(isExperimentationEnabled
        ? [
            {
              path: "redaction",
              Icon: EyeOff,
              prototype: true,
              titleKey: "settings_page_redaction_title",
              component: RedactionPage,
              descriptionKey: "settings_page_redaction_description",
            },
          ]
        : []),
    ],
  },
  {
    name: "workspace",
    path: "workspace",
    items: [
      {
        path: "account-details",
        Icon: SlidersHorizontal,
        disabled: true,
        titleKey: "settings_page_account_details_title",
        component: AccountDetailsPage,
        descriptionKey: "settings_page_account_details_description",
      },
      {
        path: "users",
        Icon: UserPlus2,
        disabled: !isHideAdminPortalEnabled,
        adminOnly: true,
        titleKey: "settings_page_users_title",
        component: UsersPage,
        descriptionKey: "settings_page_users_description",
      },
      {
        path: "groups",
        Icon: Users2,
        disabled: !isHideAdminPortalEnabled,
        adminOnly: true,
        titleKey: "settings_page_groups_title",
        component: GroupsPage,
        descriptionKey: "settings_page_groups_description",
      },
      {
        path: "roles",
        Icon: ScanFace,
        disabled: true,
        titleKey: "settings_page_roles_title",
        component: RolesPage,
        descriptionKey: "settings_page_roles_description",
      },
      {
        path: "sso",
        Icon: AppWindowMac,
        disabled: true,
        titleKey: "settings_page_sso_title",
        component: SSOPage,
        descriptionKey: "settings_page_sso_description",
      },
      {
        path: "audit-logs",
        Icon: File,
        disabled: true,
        titleKey: "settings_page_audit_logs_title",
        component: AuditLogsPage,
        descriptionKey: "settings_page_audit_logs_description",
      },
      {
        path: "webhooks",
        Icon: Webhook,
        disabled: true,
        titleKey: "settings_page_webhooks_title",
        component: WebhooksPage,
        descriptionKey: "settings_page_webhooks_description",
      },
      {
        path: "api-tokens",
        Icon: SquareCode,
        disabled: true,
        titleKey: "settings_page_api_tokens_title",
        component: APITokensPage,
        descriptionKey: "settings_page_api_tokens_description",
      },
    ],
  },
];

/**
 * LegacySettingsRedirect maps old /settings/:page urls onto the dialog search
 * param, so bookmarks keep working.
 */
function LegacySettingsRedirect() {
  const { "*": splat } = useParams();
  const search = new URLSearchParams({ settings: splat || "profile" });
  return (
    <Navigate to={{ pathname: "/chat", search: search.toString() }} replace />
  );
}

const settingsRoutes: RouteObject[] = [
  { path: "settings/*", element: <LegacySettingsRedirect /> },
];

export default settingsRoutes;
